import DefaultApiService from "./default";
import { api, authHeader } from "../configs/axiosConfigs";

const BASE = "api/sln";

const pick = (obj, ...keys) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

const unwrap = (res) => pick(res, "data", "Data") ?? null;
const isOk = (res) => {
  if (res == null || typeof res !== "object") return false;
  // Require explicit success — missing isSuccess must not count as OK.
  return res.isSuccess === true || res.IsSuccess === true;
};
const messageOf = (res, fallback) =>
  pick(res, "message", "Message") || fallback || "خطا";

const toResult = (ok, data, message) => ({
  isSuccess: ok,
  message: message || null,
  data,
});

const ContractorMasterContractApi = {
  list: async (params = {}) => {
    const query = Object.keys(params || {}).length
      ? `?${new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== "")
            .map(([k, v]) => [k, String(v)])
        ).toString()}`
      : "";
    const res = await DefaultApiService.getCustomeApi(
      `${BASE}/contractor-master-contracts${query}`
    );
    return toResult(isOk(res), unwrap(res), messageOf(res));
  },

  generate: async (contractorInfoId) => {
    const res = await DefaultApiService.postApi(
      `${BASE}/contractors/${contractorInfoId}/contractor-master-contract/generate`,
      {}
    );
    return toResult(isOk(res), unwrap(res), messageOf(res));
  },

  createRevision: async (contractId) => {
    const res = await DefaultApiService.postApi(
      `${BASE}/contractor-master-contracts/${contractId}/create-revision`,
      {}
    );
    return toResult(isOk(res), unwrap(res), messageOf(res, "ایجاد نسخه جدید ناموفق بود."));
  },

  regenerateContent: async (contractId) => {
    const res = await DefaultApiService.postApi(
      `${BASE}/contractor-master-contracts/${contractId}/regenerate-content`,
      {}
    );
    return toResult(isOk(res), unwrap(res), messageOf(res, "بازتولید محتوای قرارداد ناموفق بود."));
  },

  getById: async (contractId) => {
    const res = await DefaultApiService.getCustomeApi(
      `${BASE}/contractor-master-contracts/${contractId}`
    );
    return toResult(isOk(res), unwrap(res), messageOf(res));
  },

  approveForSignature: async (contractId) => {
    const res = await DefaultApiService.postApi(
      `${BASE}/contractor-master-contracts/${contractId}/approve-for-signature`,
      {}
    );
    return toResult(isOk(res), unwrap(res), messageOf(res));
  },

  sendForContractorSignature: async (contractId) => {
    const res = await DefaultApiService.postApi(
      `${BASE}/contractor-master-contracts/${contractId}/send-for-contractor-signature`,
      {}
    );
    return toResult(isOk(res), unwrap(res), messageOf(res));
  },

  setGuaranteeChequeDetails: async (contractId, chequeInfo = {}) => {
    const res = await DefaultApiService.postApi(
      `${BASE}/contractor-master-contracts/${contractId}/guarantee-cheque-details`,
      {
        sayadiId: chequeInfo.sayadiId || "",
        chequeSerial: chequeInfo.chequeSerial || "",
        bankName: chequeInfo.bankName || "",
        accountNumber: chequeInfo.accountNumber || "",
        branchName: chequeInfo.branchName || "",
        issuerName: chequeInfo.issuerName || "",
        chequeDate: chequeInfo.chequeDate || null,
      }
    );
    return toResult(isOk(res), unwrap(res), messageOf(res));
  },

  confirmPhysicalGuaranteeReceipt: async (contractId, parties = {}) => {
    const res = await DefaultApiService.postApi(
      `${BASE}/contractor-master-contracts/${contractId}/guarantee/confirm-physical-receipt`,
      {
        bringerName: parties.bringerName || "",
        bringerMobile: parties.bringerMobile || "",
        bringerNationalCode: parties.bringerNationalCode || "",
        officeReceiptNotes: parties.officeReceiptNotes || "",
      }
    );
    return toResult(isOk(res), unwrap(res), messageOf(res));
  },

  sendSolarentOtp: async (contractId) => {
    const res = await DefaultApiService.postApi(
      `${BASE}/contractor-master-contracts/${contractId}/solarent/send-otp`,
      {}
    );
    return toResult(isOk(res), unwrap(res), messageOf(res));
  },

  verifySolarentSign: async (contractId, verifyCode) => {
    const res = await DefaultApiService.postApi(
      `${BASE}/contractor-master-contracts/${contractId}/solarent/verify-sign`,
      { verifyCode }
    );
    return toResult(isOk(res), unwrap(res), messageOf(res));
  },

  getTimeline: async (contractId) => {
    const res = await DefaultApiService.getCustomeApi(
      `${BASE}/contractor-master-contracts/${contractId}/timeline`
    );
    return toResult(isOk(res), unwrap(res), messageOf(res));
  },

  getSignatureEvidence: async (contractId) => {
    const res = await DefaultApiService.getCustomeApi(
      `${BASE}/contractor-master-contracts/${contractId}/signature-evidence`
    );
    return toResult(isOk(res), unwrap(res), messageOf(res));
  },

  uploadGuaranteeDocument: async (contractId, file, chequeInfo = {}) => {
    if (!file) {
      return toResult(false, null, "فایل تصویر چک الزامی است.");
    }
    const formData = new FormData();
    formData.append("file", file, file.name || "guarantee-document.jpg");
    formData.append("sayadiId", String(chequeInfo.sayadiId || "").trim());
    formData.append("chequeSerial", String(chequeInfo.chequeSerial || "").trim());
    formData.append("bankName", String(chequeInfo.bankName || "").trim());
    formData.append("accountNumber", String(chequeInfo.accountNumber || "").trim());
    formData.append("branchName", String(chequeInfo.branchName || "").trim());
    formData.append("issuerName", String(chequeInfo.issuerName || "").trim());
    try {
      const res = await api.request({
        url: `${BASE}/contractor-master-contracts/${contractId}/guarantee-document/upload`,
        method: "POST",
        data: formData,
        headers: authHeader(),
      });
      const body = res?.data;
      return toResult(
        isOk(body),
        unwrap(body),
        messageOf(body, "بارگذاری مدرک ضمانت ناموفق بود.")
      );
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.message;
      return toResult(false, null, apiMsg || "بارگذاری مدرک ضمانت ناموفق بود.");
    }
  },

  downloadGuaranteeDocument: async (contractId) => {
    const res = await api.request({
      url: `${BASE}/contractor-master-contracts/${contractId}/guarantee-document`,
      method: "GET",
      headers: authHeader(),
      responseType: "blob",
    });
    return res?.data;
  },

  /**
   * Authenticated PDF fetch — never open relative FinalPdfUrl/SignedPdfUrl
   * on the SPA origin (navigates to localhost:4030 and does nothing).
   */
  downloadPdf: async (contractId, { preferSigned = true, forceRegenerate = false } = {}) => {
    if (!contractId) {
      return toResult(false, null, "شناسه قرارداد نامعتبر است.");
    }
    const signedUrl = forceRegenerate
      ? `${BASE}/contractor-master-contracts/${contractId}/download-signed-pdf?forceRegenerate=true`
      : `${BASE}/contractor-master-contracts/${contractId}/download-signed-pdf`;
    const endpoints = preferSigned
      ? [
          signedUrl,
          `${BASE}/contractor-master-contracts/${contractId}/download-pdf`,
        ]
      : [
          `${BASE}/contractor-master-contracts/${contractId}/download-pdf`,
          signedUrl,
        ];

    let lastError = null;
    for (const url of endpoints) {
      try {
        const res = await api.request({
          url,
          method: "GET",
          headers: authHeader(),
          responseType: "blob",
        });
        const blob = res?.data;
        if (!blob || !(blob instanceof Blob) || blob.size <= 0) {
          lastError = "فایل PDF خالی است.";
          continue;
        }
        // API may return JSON error as blob when status is still 200-ish handled oddly
        if (blob.type && blob.type.includes("application/json")) {
          try {
            const text = await blob.text();
            const parsed = JSON.parse(text);
            lastError =
              parsed?.message || parsed?.Message || "دانلود PDF ناموفق بود.";
          } catch {
            lastError = "دانلود PDF ناموفق بود.";
          }
          continue;
        }
        return toResult(true, blob, null);
      } catch (err) {
        lastError =
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          "دانلود PDF ناموفق بود.";
      }
    }
    return toResult(false, null, lastError || "دانلود PDF ناموفق بود.");
  },

  approveGuarantee: async (contractId) => {
    const res = await DefaultApiService.postApi(
      `${BASE}/contractor-master-contracts/${contractId}/guarantee/approve`,
      {}
    );
    return toResult(isOk(res), unwrap(res), messageOf(res, "تأیید ضمانت ناموفق بود."));
  },

  rejectGuarantee: async (contractId, reason) => {
    const res = await DefaultApiService.postApi(
      `${BASE}/contractor-master-contracts/${contractId}/guarantee/reject`,
      { reason: reason || "" }
    );
    return toResult(isOk(res), unwrap(res), messageOf(res, "رد ضمانت ناموفق بود."));
  },
};

export default ContractorMasterContractApi;
